import { staleTimes, useQuery } from '@platform/api-client';
import { pageParams, useKeysetPages } from '../useKeysetPages.ts';
import type { Operation, RequestBody, ResponseBody, TypedClient } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import type { ReportPeriod } from '../report-period.ts';
import type { RemittanceEntry } from './remittance-file.ts';

export const FINANCE_READ_ROLE = 'finance:read';
export const FINANCE_WRITE_ROLE = 'finance:write';

export type RemittanceList = ResponseBody<Operation<'/admin/finance/remittances', 'get'>>;
export type RemittanceSummary = RemittanceList['items'][number];
export type RemittanceDetail = ResponseBody<Operation<'/admin/finance/remittances/{id}', 'get'>>;
export type ImportedLine = RemittanceDetail['lines'][number];
export type RemittanceOutcome = ImportedLine['outcome'];
export type ImportRemittanceBody = RequestBody<Operation<'/admin/finance/remittances', 'post'>>;
export type RemittanceSource = ImportRemittanceBody['source'];

export const OUTCOME_VIEWS = ['all', 'attention', 'disputed', 'settled'] as const;
export type OutcomeView = (typeof OUTCOME_VIEWS)[number];

const OUTCOMES_OF_VIEW: Readonly<Record<Exclude<OutcomeView, 'all'>, readonly RemittanceOutcome[]>> = {
  attention: ['unknown', 'not_delivered', 'wrong_carrier'],
  disputed: ['disputed'],
  settled: ['settled', 'return_fee'],
};

export const linesInView = (lines: readonly ImportedLine[], view: OutcomeView): readonly ImportedLine[] =>
  view === 'all' ? lines : lines.filter((line) => OUTCOMES_OF_VIEW[view].includes(line.outcome));

export const importBodyOf = (
  carrierCode: string,
  source: RemittanceSource,
  lines: readonly RemittanceEntry[],
): ImportRemittanceBody => ({ carrierCode, source, lines: [...lines] });

export const useRemittances = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useKeysetPages<RemittanceSummary>({
    queryKey: queryKeys.remittances(tenantId, { paged: true }),
    resource: 'orders',
    fetchPage: (pageToken) => api.get('/admin/finance/remittances', { query: pageParams(pageToken) }),
  });
};

export const useRemittance = (api: TypedClient, remittanceId: string) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.remittance(tenantId, remittanceId),
    queryFn: () => api.get('/admin/finance/remittances/{id}', { path: { id: remittanceId } }),
    staleTime: staleTimes.orders,
  });
};

export type FinanceOverview = ResponseBody<Operation<'/admin/finance/overview', 'get'>>;
export type CarrierFigures = FinanceOverview['carriers'][number];
export type SettlementList = ResponseBody<Operation<'/admin/finance/settlements', 'get'>>;
export type Settlement = SettlementList['items'][number];
export type ResolveDisputeBody = RequestBody<Operation<'/admin/finance/settlements/{id}/resolve', 'post'>>;

export const SETTLEMENT_VIEWS = ['disputed', 'resolved'] as const;
export type SettlementView = (typeof SETTLEMENT_VIEWS)[number];

export const useFinanceOverview = (api: TypedClient, period: ReportPeriod) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.financeOverview(tenantId, period),
    queryFn: () => api.get('/admin/finance/overview', { query: period }),
    staleTime: staleTimes.orders,
  });
};

export const useSettlements = (api: TypedClient, status: SettlementView) => {
  const { tenantId } = usePageContext();
  return useKeysetPages<Settlement>({
    queryKey: queryKeys.settlements(tenantId, { status, paged: true }),
    resource: 'orders',
    fetchPage: (pageToken) =>
      api.get('/admin/finance/settlements', { query: { status, ...pageParams(pageToken) } }),
  });
};

export type ExpenseList = ResponseBody<Operation<'/admin/finance/expenses', 'get'>>;
export type Expense = ExpenseList['items'][number];
export type ExpenseBody = RequestBody<Operation<'/admin/finance/expenses', 'post'>>;

export const EXPENSE_VIEWS = ['active', 'archived'] as const;
export type ExpenseView = (typeof EXPENSE_VIEWS)[number];

export const useExpenses = (api: TypedClient, view: ExpenseView, company: string | null) => {
  const { tenantId } = usePageContext();
  const archived = view === 'archived' ? 'true' : 'false';
  return useKeysetPages<Expense>({
    queryKey: queryKeys.expenses(tenantId, { archived, company, paged: true }),
    resource: 'orders',
    fetchPage: (pageToken) =>
      api.get('/admin/finance/expenses', {
        query: {
          archived,
          ...(company === null ? {} : { subsidiary_id: company }),
          ...pageParams(pageToken),
        },
      }),
  });
};
